import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const EMPTY = {
  name: "", email: "", password: "", phone: "", tiktok_handle: "",
  bank_id: 3, account_number: "", account_holder: "", note: "",
};

const Field = ({ label, id, className = "", inputClassName = "", ...props }) => (
  <div className={className}>
    <Label htmlFor={id} className="text-xs">{label}</Label>
    <Input id={id} data-testid={`signup-${id}-input`} className={`mt-1 ${inputClassName}`} {...props} />
  </div>
);

export const TalentSignupForm = ({ onBack }) => {
  const [form, setForm] = useState(EMPTY);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get("/meta").then((res) => setBanks(res.data.banks)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register-talent", { ...form, bank_id: Number(form.bank_id) });
      setDone(data.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Pengajuan gagal");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-6" data-testid="signup-success">
        <div className="h-14 w-14 rounded-full bg-emerald-500/15 grid place-items-center mx-auto mb-4">
          <CheckCircle2 className="h-7 w-7 text-emerald-400" />
        </div>
        <h3 className="font-display text-lg font-bold">Pengajuan Terkirim</h3>
        <p className="text-sm text-muted-foreground mt-2">{done}</p>
        <Button data-testid="signup-back-to-login-button" onClick={onBack} className="mt-6 bg-primary hover:bg-primary/90 text-white">Kembali ke Login</Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-2 gap-3" data-testid="talent-signup-form">
      <Field className="col-span-2" id="name" label="Nama Lengkap" value={form.name} onChange={set("name")} required minLength={2} />
      <Field className="col-span-2" id="email" label="Email" type="email" value={form.email} onChange={set("email")} required />
      <Field id="phone" label="No. WhatsApp" value={form.phone} onChange={set("phone")} placeholder="08xx" required minLength={8} />
      <Field id="tiktok" label="Username TikTok" value={form.tiktok_handle} onChange={set("tiktok_handle")} placeholder="@username" required inputClassName="font-mono" />
      <div>
        <Label className="text-xs">Bank / E-wallet</Label>
        <select data-testid="signup-bank-select" value={form.bank_id} onChange={set("bank_id")} className="mt-1 w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm">
          {banks.map((b) => <option key={b.bank_id} value={b.bank_id} className="bg-card">{b.name}</option>)}
        </select>
      </div>
      <Field id="account" label="No. Rekening / HP" value={form.account_number} onChange={set("account_number")} />
      <Field className="col-span-2" id="password" label="Buat Password (min. 8 karakter)" type="password" value={form.password} onChange={set("password")} required minLength={8} />
      <div className="col-span-2">
        <Label className="text-xs">Ceritakan konten Anda (opsional)</Label>
        <Textarea data-testid="signup-note-input" value={form.note} onChange={set("note")} maxLength={500} placeholder="Niche, jumlah followers, produk yang biasa dipromosikan..." className="mt-1 h-20" />
      </div>
      <Button data-testid="signup-submit-button" type="submit" disabled={loading} className="col-span-2 w-full bg-primary hover:bg-primary/90 text-white font-semibold h-11">
        {loading ? "Mengirim..." : "Ajukan Pendaftaran"}
      </Button>
    </form>
  );
};
